import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Empty, Space, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import { dedicatedUserApi as api, isOk, errMsg, arr, STATUS, DC_LABEL } from '../../../shared/services/dedicatedService';

const { Text } = Typography;

export const DedicatedStatusTag = ({ status }) => {
    const { t } = useTranslation();
    const [color, label] = STATUS[status] || ['default', status];
    return <Tag color={color}>{t(label)}</Tag>;
};

const MyDedicated = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        const r = await api.list();
        setLoading(false);
        if (isOk(r)) setList(arr(r.data?.list));
        else if (!silent) message.error(errMsg(r, t('Failed to load')));
    }, [t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        clearTimeout(timer.current);
        if (list.some((x) => ['ordering', 'provisioning'].includes(x.status))) timer.current = setTimeout(() => load(true), 30000);
        return () => clearTimeout(timer.current);
    }, [list, load]);

    const goOrder = () => navigate(userPath('/dedicated/order'));

    const columns = [
        {
            title: t('Server'), key: 'name', render: (_, s) => (
                <Space direction="vertical" size={2}>
                    <Space size={8} wrap><Text strong>{s.hostname}</Text><DedicatedStatusTag status={s.status} /></Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>{s.name}</Text>
                </Space>
            ),
        },
        { title: t('Location'), dataIndex: 'data_center', render: (v) => t(DC_LABEL[v] || v) },
        { title: t('IP address'), dataIndex: 'ip', render: (v) => (v ? <Text copyable style={{ fontFamily: 'monospace' }}>{v}</Text> : '-') },
        { title: t('Created'), dataIndex: 'created_at', render: (v) => (v ? String(v).slice(0, 10) : '-') },
    ];

    return (
        <div>
            <PageHeader
                title={t('Dedicated Servers')}
                extra={<>
                    <Button icon={<SyncOutlined />} onClick={() => load()} loading={loading} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={goOrder}>{t('Order')}</Button>
                </>}
            />
            <Card styles={{ body: { padding: 0 } }}>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={list}
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    onRow={rowLink(navigate, (x) => `/dedicated/${x.id}`)}
                    rowClassName={() => ROW_LINK_CLASS}
                    pagination={list.length > 20 ? { pageSize: 20 } : false}
                    locale={{
                        emptyText: (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No dedicated servers yet')}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={goOrder}>{t('Order')}</Button>
                            </Empty>
                        ),
                    }}
                />
            </Card>
        </div>
    );
};

export default MyDedicated;
